import { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "rebass/styled-components";

import { StyledMobileAppPromo } from "./styledComponents";
import { useAds } from "../../../Providers";

const AD_SLOT_ID = "side-rail-bottom-left";

const getAdsLogging = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get("adsLogging") === "true";
};

const log = (...args: unknown[]) => {
  if (getAdsLogging()) {
    console.log("[MobileAppPromo]", ...args);
  }
};

const MobileAppPromo = () => {
  const { gpt, gptStatus, config } = useAds();
  const slotRef = useRef<typeof window.googletag.Slot | null>(null);
  const isInitializingRef = useRef(false);
  const [domReady, setDomReady] = useState(false);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      log("DOM container mounted");
      setDomReady(true);
    }
  }, []);

  useEffect(() => {
    const displayConfig = config?.display;
    const isEnabled = displayConfig?.enabled === true;

    log("Effect triggered", {
      gptExists: !!gpt,
      gptStatus,
      displayConfigExists: !!displayConfig,
      isEnabled,
      domReady,
      hasSlot: !!slotRef.current,
      isInitializing: isInitializingRef.current,
    });

    if (!gpt || gptStatus !== "ready") {
      log("GPT not ready, skipping");
      return;
    }

    if (!displayConfig || !isEnabled) {
      log("Display ads not enabled, skipping");
      return;
    }

    if (!domReady) {
      log("DOM not ready, skipping");
      return;
    }

    if (slotRef.current || isInitializingRef.current) {
      log("Slot already exists or initializing, skipping");
      return;
    }

    isInitializingRef.current = true;
    log("Starting slot initialization");

    gpt.cmd.push(() => {
      if (slotRef.current) {
        log("Slot was created while waiting in queue, skipping");
        isInitializingRef.current = false;
        return;
      }

      const adElement = document.getElementById(AD_SLOT_ID);
      if (!adElement) {
        log("Ad container element not found in DOM");
        isInitializingRef.current = false;
        return;
      }

      log("Defining slot", {
        path: `/${displayConfig.networkId}/${displayConfig.sideRailAdUnit}`,
        size: [[420, 200]],
        slotId: AD_SLOT_ID,
      });

      const slot = gpt.defineSlot(
        `/${displayConfig.networkId}/${displayConfig.sideRailAdUnit}`,
        [[420, 200]],
        AD_SLOT_ID,
      );

      if (!slot) {
        log("Failed to define slot - defineSlot returned null");
        isInitializingRef.current = false;
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const testAds = params.get("testAds");
      if (testAds) {
        log("Setting test targeting", testAds);
        slot.setTargeting("test", testAds);
      }

      const viewerSource = window.location.href.includes("iframe")
        ? "iframe"
        : "website";
      log("Setting viewer_source targeting", viewerSource);
      slot.setTargeting("viewer_source", viewerSource);

      slot.addService(gpt.pubads());
      slotRef.current = slot;

      log("Calling gpt.display()");
      gpt.display(AD_SLOT_ID);

      log("Refreshing slot to fetch ad");
      gpt.pubads().refresh([slot]);

      log("Slot initialization complete");
      isInitializingRef.current = false;
    });
  }, [gpt, gptStatus, config?.display, domReady]);

  useEffect(() => {
    const currentGpt = gpt;
    return () => {
      if (currentGpt && slotRef.current) {
        const slotToDestroy = slotRef.current;
        log("Cleanup: destroying slot");
        currentGpt.cmd.push(() => {
          currentGpt.destroySlots([slotToDestroy]);
        });
        slotRef.current = null;
      }
    };
  }, [gpt]);

  return (
    <StyledMobileAppPromo>
      <Box ref={containerRef} id={AD_SLOT_ID} style={{ width: "100%" }} />
    </StyledMobileAppPromo>
  );
};

export { MobileAppPromo };
